import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';
import { useConfirm } from '../../components/common/ConfirmModal';
import useEscapeKey from '../../hooks/useEscapeKey';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';
import EmptyState from '../../components/common/EmptyState';
import { SkeletonRow } from '../../components/common/Skeleton';
import { getClasses, createClass, updateClass, deleteClass } from '../../services/classService';
import { getTrainers } from '../../services/trainerService';
import { getSettings } from '../../services/settingsService';
import { DEFAULT_AGE_GROUPS } from '../../utils/age';
import SuspendedAccessPanel from '../../components/common/SuspendedAccessPanel';
import { isClubSuspended, isClubCancelled } from '../../utils/clubError';
import { CLASS_TYPE_PILL, CLASS_TYPE_CARD } from '../../utils/classType';
import {
  MdAdd, MdFitnessCenter, MdClose, MdDelete, MdEdit, MdList, MdCalendarViewWeek, MdOpenInNew,
  MdChecklist, MdSearch, MdExpandMore,
} from 'react-icons/md';

const ALL_DAYS    = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CLASS_TYPES = ['Morning', 'Evening', 'Weekend', 'Special'];

const TYPE_PILL = CLASS_TYPE_PILL;

const timeStr = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr  = parseInt(h);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
};

const WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function DayDots({ days = [] }) {
  if (!days.length) return <span className="text-xs text-subtle">—</span>;
  const active = new Set(days);
  return (
    <div className="flex items-center gap-1" title={days.map(d => d.slice(0, 3)).join(', ')}>
      {WEEK.map((day, i) => {
        const on = active.has(day);
        return (
          <span
            key={day}
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
              on ? 'bg-secondary text-card' : 'border border-divider text-subtle'
            }`}
          >
            {WEEK_LETTERS[i]}
          </span>
        );
      })}
    </div>
  );
}

function AddClassModal({ cls, onClose, onSuccess }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const isEdit = !!cls;
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: isEdit
      ? {
          name: cls.name || '', discipline: cls.discipline || '', type: cls.type || 'Morning',
          trainer: cls.trainer?._id || cls.trainer || '',
          startTime: cls.startTime || '', endTime: cls.endTime || '',
          maxStudents: cls.maxStudents ?? '', room: cls.room || '',
        }
      : { type: 'Morning', maxStudents: '' },
  });
  const [days, setDays]           = useState(isEdit ? (cls.days || []) : []);
  const [ageGroupNames, setAgeGroupNames] = useState(isEdit ? (cls.suitableAgeGroups || []) : []);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  // Keyed on the name text itself (not a plain boolean) so dismissing the notice for "Beginner
  // Youth" doesn't also hide it if the admin then types a DIFFERENT duplicate name.
  const [dismissedForName, setDismissedForName] = useState('');

  const { data: trainerData } = useQuery({
    queryKey: ['trainers-list'],
    queryFn:  () => getTrainers({ limit: 100, isActive: 'true' }).then(r => r.data.trainers),
  });
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn:  () => getSettings().then(r => r.data.settings),
    staleTime: 1000 * 60 * 10,
  });
  const ageGroups = settingsData?.ageGroups?.length ? settingsData.ageGroups : DEFAULT_AGE_GROUPS;

  // Same list every other class picker in the app already loads (['classes-all'], limit 100) —
  // used here only for a non-blocking "a class with this name already exists" notice, never to
  // block submission. Shares the query cache if the admin had another picker open this session.
  const { data: allClasses } = useQuery({
    queryKey: ['classes-all'],
    queryFn:  () => getClasses({ limit: 100 }).then(r => r.data.classes),
    staleTime: 60000,
  });
  const nameValue = watch('name') || '';
  const sameNameClasses = (allClasses || []).filter(c =>
    c._id !== cls?._id && c.name.trim().toLowerCase() === nameValue.trim().toLowerCase()
  );
  const existingDaysForName = [...new Set(sameNameClasses.flatMap(c => c.days || []))];

  const toggleDay = (day) =>
    setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  const toggleAgeGroup = (name) =>
    setAgeGroupNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const onSubmit = async (data) => {
    setServerError('');
    setSubmitting(true);
    try {
      // Blank = no limit (null) — not a literal 30. An unset class is genuinely unbounded
      // until an admin deliberately chooses a number.
      const payload = {
        ...data, days, suitableAgeGroups: ageGroupNames,
        maxStudents: data.maxStudents === '' || data.maxStudents == null ? null : Number(data.maxStudents),
      };
      if (isEdit) {
        // Send `trainer` as-is ('' clears the assignment); backend allow-list handles the rest.
        await updateClass(cls._id, payload);
        toast.success('Class updated');
      } else {
        await createClass({ ...payload, trainer: data.trainer || undefined });
        toast.success('Class created');
        reset();
      }
      onSuccess();
    } catch (err) {
      setServerError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} class`);
    } finally {
      setSubmitting(false);
    }
  };

  useEscapeKey(onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary text-base">{isEdit ? 'Edit Class' : 'Add New Class'}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="overflow-y-auto px-6 py-4 space-y-4">
          {serverError && (
            <div className="bg-red-50 text-primary text-sm rounded-xl px-4 py-3">{serverError}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Class Name *"
              placeholder="e.g. Junior Taekwondo"
              {...register('name', { required: 'Class name is required' })}
              error={errors.name?.message}
            />
            <Input
              label="Discipline"
              placeholder="Taekwondo"
              {...register('discipline')}
            />
          </div>

          {sameNameClasses.length > 0 && dismissedForName !== nameValue.trim() && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-amber-800">
                  A class called <strong>{nameValue.trim()}</strong> already exists
                  {existingDaysForName.length > 0 && ` (${existingDaysForName.map(d => d.slice(0, 3)).join(', ')})`}.
                  Did you mean to add a day to that one instead?
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDismissedForName(nameValue.trim())}
                className="text-xs font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap flex-shrink-0"
              >
                Continue anyway
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Type</label>
              <select {...register('type')} className="w-full bg-card border border-divider rounded-full px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-divider">
                {CLASS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Trainer *</label>
              <select
                {...register('trainer', { required: 'Please assign a trainer' })}
                className={`w-full bg-card border rounded-full px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-divider ${errors.trainer ? 'border-red-500/50' : 'border-divider'}`}
              >
                <option value="">Select a trainer</option>
                {trainerData?.map(t => (
                  <option key={t._id} value={t._id}>{t.fullName}</option>
                ))}
              </select>
              {errors.trainer && <p className="text-xs text-red-400 mt-1">{errors.trainer.message}</p>}
            </div>
          </div>

          {/* Days checkboxes — multi-select is the norm (most classes meet 2+ days/week), so
              say so explicitly rather than leaving it to be discovered by accident. */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs font-medium text-muted">Days <span className="text-subtle font-normal">— tap all that apply</span></p>
              {days.length > 0 && (
                <span className="text-[11px] font-medium text-secondary">{days.length} selected</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_DAYS.map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    days.includes(day)
                      ? 'bg-secondary text-card border-secondary'
                      : 'bg-card text-muted border-divider hover:text-secondary hover:border-secondary'
                  }`}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-subtle mt-2">
              If this class meets more than once a week, select every day here — one class
              covers all of them. Sessions appear automatically on these days, ready for attendance.
            </p>
          </div>

          {/* Suitable age groups — optional recommendation, never a restriction */}
          <div>
            <p className="text-xs font-medium text-muted mb-2">Suitable Age Groups (optional)</p>
            <div className="flex flex-wrap gap-2">
              {ageGroups.map(g => (
                <button
                  key={g.name}
                  type="button"
                  onClick={() => toggleAgeGroup(g.name)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    ageGroupNames.includes(g.name)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-card text-muted border-divider hover:text-secondary hover:border-primary/40'
                  }`}
                >
                  {g.name} ({g.minAge}{g.maxAge != null ? `–${g.maxAge}` : '+'})
                </button>
              ))}
            </div>
            <p className="text-[11px] text-subtle mt-2">
              Used only to recommend this class when adding a student — never restricts enrollment.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input label="Start Time" type="time" {...register('startTime')} />
            <Input label="End Time"   type="time" {...register('endTime')} />
            <Input label={`Max ${v.members}`} type="number" min="1" placeholder="No limit" {...register('maxStudents')} />
          </div>
          <p className="text-[11px] text-subtle -mt-1">Leave blank for no limit.</p>

          <Input label="Room / Location" placeholder="e.g. Dojo Hall A" {...register('room')} />

          <div className="flex justify-end gap-2 pt-2 border-t border-divider">
            <Button type="button" variant="ghostPremium" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="inverted"
              disabled={submitting || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            >{submitting ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Class')}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// A compact class card used in the weekly schedule grid
function ClassCard({ cls, onDelete, onEdit }) {
  const { isReadOnly } = useImpersonation();
  const [confirm, confirmModal] = useConfirm();
  const TYPE_COLORS = CLASS_TYPE_CARD;
  return (
    <div className={`rounded-xl border p-2.5 text-xs mb-2 ${TYPE_COLORS[cls.type] || 'bg-gray-50 border-divider'}`}>
      <p className="font-semibold truncate">{cls.name}</p>
      {cls.startTime && <p className="mt-0.5 opacity-70">{timeStr(cls.startTime)} – {timeStr(cls.endTime)}</p>}
      {cls.trainer && <p className="mt-0.5 opacity-70 truncate">{cls.trainer.fullName}</p>}
      <div className="mt-1.5 flex items-center gap-2">
        <button
          onClick={() => { if (!isReadOnly) onEdit?.(cls); }}
          disabled={isReadOnly}
          title={isReadOnly ? 'Disabled: read-only support mode' : 'Edit class'}
          className="text-subtle hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MdEdit />
        </button>
        <button
          onClick={async () => { if (await confirm({ title: 'Remove class?', message: `Remove "${cls.name}" from the schedule?`, confirmLabel: 'Remove' })) onDelete(cls._id); }}
          disabled={isReadOnly}
          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          className="text-subtle hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MdDelete />
        </button>
      </div>
      {confirmModal}
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'name:asc',       label: 'Name (A–Z)' },
  { value: 'name:desc',      label: 'Name (Z–A)' },
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc',  label: 'Oldest first' },
  { value: 'startTime:asc',  label: 'Earliest time' },
  { value: 'startTime:desc', label: 'Latest time' },
];

function Classes() {
  document.title = 'Classes — TrainAxis';
  const { isReadOnly } = useImpersonation();
  const [confirm, confirmModal] = useConfirm();
  const queryClient            = useQueryClient();
  const navigate               = useNavigate();
  const [view, setView]        = useState('list');  // 'list' | 'schedule'
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [editClass, setEditClass]   = useState(null); // class being edited (null = closed)

  // Pagination / sorting / search (list view)
  const [page, setPage]                 = useState(1);
  const [limit, setLimit]               = useState(10);
  const [sortBy, setSortBy]             = useState('name');
  const [sortOrder, setSortOrder]       = useState('asc');
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebounced] = useState('');
  const searchTimer = useRef(null);

  // Paginated list query
  const { data, isPending, error } = useQuery({
    queryKey: ['classes', page, limit, sortBy, sortOrder, debouncedSearch, typeFilter],
    queryFn: () => {
      const params = { page, limit, sortBy, sortOrder, isActive: 'true' };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter)      params.type   = typeFilter;
      return getClasses(params).then(r => r.data);
    },
    placeholderData: keepPreviousData,
  });

  // Full (unpaginated) query for the weekly schedule view
  const { data: schedData } = useQuery({
    queryKey: ['classes-schedule', typeFilter],
    queryFn: () => {
      const params = { isActive: 'true' };
      if (typeFilter) params.type = typeFilter;
      return getClasses(params).then(r => r.data);
    },
    enabled: view === 'schedule',
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteClass(id),
    onSuccess:  () => {
      toast.success('Class removed');
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['classes-schedule'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to remove class'),
  });

  // Robust parsing — tolerant of any response shape
  const classes = data?.classes || data?.data || [];
  const total   = data?.pagination?.total ?? classes.length;
  const pages   = data?.pagination?.totalPages ?? 1;

  // Group ALL classes by day for the weekly schedule view
  const schedClasses = schedData?.classes || schedData?.data || [];
  const byDay = ALL_DAYS.reduce((acc, day) => {
    acc[day] = schedClasses.filter(c => c.days?.includes(day));
    return acc;
  }, {});

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebounced(val); setPage(1); }, 400);
  };

  // If an action empties the current page, step back one page
  useEffect(() => {
    if (!isPending && total > 0 && classes.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, total, classes.length, page]);

  // Club access blocked (subscription suspended/expired) — clear panel, not empty schedule.
  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div>
      {confirmModal}
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <p className="text-subtle text-sm">{total} active class{total !== 1 ? 'es' : ''}</p>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${view === 'list' ? 'bg-card text-secondary shadow-sm' : 'text-muted'}`}
            >
              <MdList /> List
            </button>
            <button
              onClick={() => setView('schedule')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${view === 'schedule' ? 'bg-card text-secondary shadow-sm' : 'text-muted'}`}
            >
              <MdCalendarViewWeek /> Schedule
            </button>
          </div>
          <Button
            variant="inverted"
            onClick={() => setShowModal(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          ><MdAdd className="text-lg" /> Add Class</Button>
        </div>
      </div>

      {/* Type filter pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['', ...CLASS_TYPES].map(t => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              typeFilter === t
                ? 'bg-secondary text-card border-secondary'
                : 'bg-card text-muted border-divider hover:text-secondary hover:border-secondary'
            }`}
          >
            {t || 'All Types'}
          </button>
        ))}
      </div>

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <>
        {/* Toolbar: search · sort · rows per page */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" />
            <input
              value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search class or trainer…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-divider rounded-full focus:outline-none focus:ring-2 focus:ring-divider"
            />
          </div>
          <div className="relative">
          <select
            value={`${sortBy}:${sortOrder}`}
            onChange={e => { const [b, o] = e.target.value.split(':'); setSortBy(b); setSortOrder(o); setPage(1); }}
            className="appearance-none pl-4 pr-9 py-2 text-sm bg-card border border-divider rounded-full truncate focus:outline-none focus:ring-2 focus:ring-divider"
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
          </div>
          <div className="relative">
          <select
            value={limit}
            onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            className="appearance-none pl-4 pr-9 py-2 text-sm bg-card border border-divider rounded-full truncate focus:outline-none focus:ring-2 focus:ring-divider"
            title="Rows per page"
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
          </div>
        </div>

        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-divider text-xs text-subtle font-semibold uppercase tracking-wide">
                <th className="text-left px-5 py-3">Class</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Type</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Days</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Time</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Trainer</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Max</th>
                <th className="text-left px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {isPending
                ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={7} />)
                : !classes.length
                  ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          icon={MdFitnessCenter}
                          title="No classes yet"
                          description="Create your first class before you can take attendance or schedule sessions."
                          primaryAction={{ label: 'Add Class', icon: MdAdd, onClick: () => { if (!isReadOnly) setShowModal(true); } }}
                          secondaryAction={{ label: 'View Setup Checklist', icon: MdChecklist, to: '/admin/dashboard' }}
                        />
                      </td>
                    </tr>
                  )
                  : classes.map(c => (
                    <tr key={c._id} onClick={() => navigate(`/admin/classes/${c._id}`)} className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors cursor-pointer">
                      <td className="px-5 py-3">
                        <p className="text-sm font-semibold text-secondary">{c.name}</p>
                        <p className="text-xs text-muted">{c.discipline}</p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${TYPE_PILL[c.type] || 'bg-gray-100 text-muted'}`}>{c.type}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <DayDots days={c.days} />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-muted whitespace-nowrap">
                        {c.startTime ? `${timeStr(c.startTime)} – ${timeStr(c.endTime)}` : '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted">
                        {c.trainer?.fullName || '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted">
                        {c.capacitySet ? (
                          <span className={c.enrolledCount >= c.capacity ? 'text-amber-500 font-semibold' : ''}>
                            {c.enrolledCount ?? 0}/{c.capacity}
                          </span>
                        ) : (
                          <>
                            {c.enrolledCount ?? 0} enrolled <span className="text-subtle">· no limit set</span>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); if (!isReadOnly) setEditClass(c); }}
                            disabled={isReadOnly}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={isReadOnly ? 'Disabled: read-only support mode' : 'Edit class'}
                          >
                            <MdEdit className="text-lg" />
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (await confirm({ title: 'Remove class?', message: `Remove "${c.name}"? This cannot be undone.`, confirmLabel: 'Remove' })) deleteMutation.mutate(c._id);
                            }}
                            disabled={deleteMutation.isPending || isReadOnly}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={isReadOnly ? 'Disabled: read-only support mode' : 'Remove class'}
                          >
                            <MdDelete className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
          </div>
          {pages > 1 && (
            <Pagination
              page={page}
              pages={pages}
              total={total}
              limit={limit}
              onChange={(p) => setPage(p)}
            />
          )}
        </Card>
        </>
      )}

      {/* ── WEEKLY SCHEDULE VIEW ── */}
      {view === 'schedule' && (
        <div className="overflow-x-auto">
          <div className="grid grid-cols-7 gap-3 min-w-[700px]">
            {ALL_DAYS.map(day => (
              <div key={day}>
                <div className="bg-gray-50 rounded-xl px-2 py-2 text-center mb-2">
                  <p className="text-xs font-bold text-secondary">{day.slice(0, 3).toUpperCase()}</p>
                  <p className="text-[10px] text-subtle">{byDay[day]?.length || 0} class{byDay[day]?.length !== 1 ? 'es' : ''}</p>
                </div>
                {!schedData
                  ? <div className="h-16 bg-gray-100 animate-pulse rounded-xl" />
                  : byDay[day]?.length
                    ? byDay[day].map(c => (
                        <ClassCard key={c._id} cls={c} onDelete={(id) => deleteMutation.mutate(id)} onEdit={(cl) => setEditClass(cl)} />
                      ))
                    : (
                      <div className="rounded-xl border border-dashed border-divider p-3 text-center">
                        <p className="text-[10px] text-subtle">No classes</p>
                      </div>
                    )
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {(showModal || editClass) && (
        <AddClassModal
          cls={editClass}
          onClose={() => { setShowModal(false); setEditClass(null); }}
          onSuccess={() => {
            setShowModal(false);
            setEditClass(null);
            queryClient.invalidateQueries({ queryKey: ['classes'] });
            queryClient.invalidateQueries({ queryKey: ['classes-schedule'] });
          }}
        />
      )}
    </div>
  );
}

export default Classes;
